/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * importApi — loading a timing company's results file into a race.
 *
 * NO NEW BACKEND. The file is read in the browser and the rows are written with
 * the batch endpoint the platform already has: /arcee/application/table/save/
 * records/, whose execute_batch already upserts — an operation carrying an `id`
 * updates, one without inserts, all inside one transaction. An importer in the
 * backend would have been a second copy of that.
 *
 * HOW A BIB BECOMES A RESULT. A timing file names a finisher by bib and nothing
 * else, while event_result is keyed by registration_id — its only UNIQUE column
 * and the reason one person cannot hold two official times for one race. So the
 * bib is carried on the REGISTRATION, in the `extras` json that already holds
 * seat_no and checkout_id, and the browser matches file → entrant → result.
 *
 * WHY MATCHING HAPPENS HERE AND NOT IN SQL. The platform's filters cannot look
 * inside a json column (filters_helper allows only is_empty/is_not_empty on
 * one), so there is no server-side "where extras->>'bib' = ...". The entrant
 * list is read once, by page, and matched in memory. For a race this is a few
 * hundred rows and costs nothing; it is listed here because it is the reason
 * the code looks like this.
 *
 * SCOPED TO THIS RACE, ALWAYS. Bib numbers restart per race — bib 101 in the
 * 10 km and bib 101 in the marathon are two people — so the entrant list is
 * fetched for ONE event_activity_id and a bib is only ever looked up inside it.
 *
 * WHAT A CLOSED TAB COSTS. The browser drives the chunks, so closing it half
 * way leaves some rows written and some not. That is recoverable and not
 * corrupting: every row is an upsert keyed on the registration, so re-uploading
 * the same file finishes the job and changes nothing it already did.
 */

import envConfig from '../../../config/envConfig';
import {
  RESULT_TABLE, REGISTRATION_TABLE, readError,
} from './resultsApi';

/**
 * Rows per write. 500 is the platform's own ceiling (MAX_ROWS in batch_save),
 * so this sits exactly at it: a 10,000-row file goes in 20 batches instead of
 * 50, and the request count drops with it.
 *
 * Raised from 200 on 2026-11-03, deliberately and with the risk named. A batch
 * is ONE transaction and the `record.before_created` hook runs per row inside
 * it, so a bigger batch is a longer-held transaction. `attachApi` carries a
 * measured note about an oversized batch blowing that hook's timeout and
 * taking every row in the batch down with it — which is why the ATTACH path
 * still writes 25 at a time and is not changed here.
 *
 * The import is safer than the attach at this size for a specific reason: most
 * of its rows are UPDATES of results that already exist, and the hook is a
 * before-CREATED hook, so it does not run on them at all. The rows that do
 * create are the cheap ones — a bib, a time and a status, with no activity to
 * geo-check. Measured at 500: 10,000 rows, no refusals.
 *
 * If a batch is ever refused for time rather than for data, this is the first
 * number to put back to 200.
 */
export const CHUNK = 500;

/** Entrants per read while paging the registration list. */
const ENTRANT_PAGE = 200;

/** The columns we offer to read, in the order the chooser shows them. */
export const IMPORTABLE_COLUMNS = [
  { column: 'bib', label: 'Bib', required: true },
  { column: 'athlete_name', label: 'Name' },
  { column: 'duration_seconds', label: 'Finish time' },
  { column: 'status', label: 'Status' },
  { column: 'distance_meters', label: 'Distance (km)' },
  { column: 'position_overall', label: 'Position' },
  { column: 'started_at', label: 'Start time' },
  { column: 'finished_at', label: 'Finish clock time' },
];

/**
 * What the words in somebody else's Status column mean in ours.
 *
 * Anything unrecognised becomes OTHER rather than FINISHED, because a finish is
 * what puts somebody on the leaderboard and a word we could not read is not
 * evidence that they finished.
 */
const STATUS_WORDS = {
  FINISHED: 'FINISHED', FINISH: 'FINISHED', FIN: 'FINISHED', OK: 'FINISHED',
  DNF: 'DID_NOT_FINISH', 'DID NOT FINISH': 'DID_NOT_FINISH',
  DNS: 'NOT_STARTED', 'DID NOT START': 'NOT_STARTED',
  INJURED: 'INJURED', INJ: 'INJURED',
};

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

const blank = (v) => v === null || v === undefined || String(v).trim() === '';

/** One rejected row, carrying the line an organizer can scroll to. */
class Rejected extends Error {}

// ── reading the file ────────────────────────────────────────────────────────

/** Enough CSV for real files: quoted cells, so "Surname, Firstname" survives. */
function splitLine(line) {
  const out = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cell += '"'; i += 1; } else if (ch === '"') { quoted = false; } else { cell += ch; }
    } else if (ch === '"') { quoted = true; } else if (ch === ',') { out.push(cell); cell = ''; } else { cell += ch; }
  }
  out.push(cell);
  return out.map((c) => c.trim());
}

/** CSV text → headers and every data row, each tagged with its line number. */
export function parseCsv(text) {
  // Excel writes a BOM, which otherwise becomes part of the first header and
  // makes the column chooser miss it for reasons nobody can see on screen.
  const clean = text.startsWith('﻿') ? text.slice(1) : text;
  const lines = clean.split(/\r?\n/);
  const data = [];
  let headers = [];
  lines.forEach((raw, index) => {
    if (raw.trim() === '') return;
    if (!headers.length) { headers = splitLine(raw); return; }
    const cells = splitLine(raw);
    // The line number as a spreadsheet counts it, so "line 7" means line 7.
    data.push({ line: index + 1, cells });
  });
  return { headers, rows: data };
}

/** The header row and the first few rows, for the chooser to sit over. */
export function previewCsv(text) {
  const { headers, rows } = parseCsv(text);
  return { headers, rows: rows.slice(0, 3).map((r) => r.cells), count: rows.length };
}

const GUESSES = [
  [/^bib|bib ?(no|number|#)/i, 'bib'],
  [/(runner|athlete|participant) ?name|^name$/i, 'athlete_name'],
  [/chip|net ?time/i, 'duration_seconds'],
  [/^time$|duration|^finish ?time$/i, 'duration_seconds'],
  [/status|^result$/i, 'status'],
  [/dist/i, 'distance_meters'],
  [/rank|position|place|^pos$/i, 'position_overall'],
  [/start ?(time|clock)/i, 'started_at'],
  [/finish ?clock/i, 'finished_at'],
];

/**
 * Guess which of our columns each header feeds.
 *
 * A guess only — every one is shown for the organizer to correct before
 * anything is written. Guessing badly costs one dropdown; not guessing costs
 * eight. Gun time is deliberately NOT guessed as the finish time: chip time is
 * the one that counts, and silently picking the wrong one is the single most
 * damaging thing this screen could do.
 */
export function guessMapping(headers) {
  const mapping = {};
  const taken = new Set();
  headers.forEach((header) => {
    const hit = GUESSES.find(([pattern]) => pattern.test(header));
    if (hit && !taken.has(hit[1])) { mapping[header] = hit[1]; taken.add(hit[1]); }
  });
  return mapping;
}

// ── converting values ───────────────────────────────────────────────────────

const HMS = /^(?:(\d+):)?(\d{1,2}):(\d{1,2}(?:\.\d+)?)$/;

/** "3:05:00", "45:12" or "11100" → whole seconds. */
function toSeconds(value, header) {
  if (blank(value)) return null;
  const text = String(value).trim();
  if (/^\d+$/.test(text)) return Number(text);
  const m = HMS.exec(text);
  if (!m) throw new Rejected(`${header}: "${value}" is not a time — expected 3:05:00 or 45:12`);
  // Fractional seconds are dropped: the column is a whole number, and showing
  // hundredths we were never given would be inventing precision.
  return Number(m[1] || 0) * 3600 + Number(m[2]) * 60 + Math.trunc(Number(m[3]));
}

function toNumber(value, header) {
  if (blank(value)) return null;
  const n = Number(String(value).trim());
  if (Number.isNaN(n)) throw new Rejected(`${header}: "${value}" is not a number`);
  return n;
}

function toDate(value, header) {
  if (blank(value)) return null;
  const text = String(value).trim().replace(' ', 'T').replace('Z', '');
  const at = new Date(text);
  if (Number.isNaN(at.getTime())) throw new Rejected(`${header}: "${value}" is not a date and time`);
  return text;
}

function toStatus(value) {
  if (blank(value)) return 'FINISHED';
  return STATUS_WORDS[String(value).trim().toUpperCase()] || 'OTHER';
}

/** One CSV row → the columns of an event_result, before the entrant is known. */
function valuesFor(cells, headers, mapping) {
  const values = {};
  headers.forEach((header, i) => {
    const column = mapping[header];
    if (!column) return;
    const raw = cells[i];
    switch (column) {
      // Kept as text, and trimmed only: bib "0042" is not bib 42, and the
      // leading zeros are part of the identity rather than formatting.
      case 'bib': values.bib = blank(raw) ? null : String(raw).trim(); break;
      case 'athlete_name': values.athlete_name = blank(raw) ? null : String(raw).trim(); break;
      case 'duration_seconds': values.duration_seconds = toSeconds(raw, header); break;
      case 'distance_meters': {
        const km = toNumber(raw, header);
        values.distance_meters = km === null ? null : km * 1000;
        break;
      }
      case 'position_overall': values.position_overall = toNumber(raw, header); break;
      case 'started_at': values.started_at = toDate(raw, header); break;
      case 'finished_at': values.finished_at = toDate(raw, header); break;
      case 'status': values.status = toStatus(raw); break;
      default: break;
    }
  });
  if (values.status === undefined) values.status = 'FINISHED';
  return values;
}

// ── reading what is already there ───────────────────────────────────────────

async function fetchPage(api, body, signal) {
  const resp = await api.apiCall('/arcee/application/table/fetch/', {
    method: 'POST',
    body: JSON.stringify({ ...scope(), ...body }),
    signal,
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not read this race.'));
  const payload = await resp.json();
  const rows = Array.isArray(payload) ? payload : (payload?.records || payload?.results || []);
  const count = typeof payload?.count === 'number' ? payload.count : rows.length;
  return { rows, count };
}

/**
 * Every entrant of one race, by page.
 *
 * The generic table read, not a saved analytics query: analytics is capped at
 * 500 rows (ROW_CAP), which a marathon passes. RLS already lets an organizer
 * read the registrations of an event they created, so this needs nothing new.
 */
export async function fetchEntrants(api, { eventActivityId, signal } = {}) {
  const all = [];
  for (let page = 1; ; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { rows, count } = await fetchPage(api, {
      table_name: REGISTRATION_TABLE,
      page,
      page_size: ENTRANT_PAGE,
      filters: [{
        column_key: 'event_activity_id', filter_type: 'equals', column_value: Number(eventActivityId),
      }],
    }, signal);
    all.push(...rows);
    if (rows.length < ENTRANT_PAGE || all.length >= count) break;
  }
  return all;
}

/** bib → entrant, for the entrants that have one. Duplicates are reported. */
export function bibIndex(entrants) {
  const byBib = new Map();
  const duplicates = new Set();
  entrants.forEach((entrant) => {
    let extras = entrant.extras;
    if (typeof extras === 'string') {
      try { extras = JSON.parse(extras); } catch { extras = null; }
    }
    const bib = extras && !blank(extras.bib) ? String(extras.bib).trim() : null;
    if (!bib) return;
    if (byBib.has(bib)) duplicates.add(bib);
    byBib.set(bib, entrant);
  });
  return { byBib, duplicates };
}

// ── building the write ──────────────────────────────────────────────────────

/**
 * Turn parsed rows into batch operations, and say why the rest were left out.
 *
 * `existingByRegistration` maps registration_id → the result row already
 * stored, which is what decides insert from update and therefore what the
 * timing source becomes.
 */
export function buildOperations({
  rows, headers, mapping, byBib, duplicates, existingByRegistration,
  eventActivityId, timingSourceIds,
}) {
  const rowsOut = [];
  const rejections = [];
  const seen = new Map();

  rows.forEach(({ line, cells }) => {
    try {
      const values = valuesFor(cells, headers, mapping);
      if (blank(values.bib)) throw new Rejected('this row has no bib number');

      if (duplicates.has(values.bib)) {
        throw new Rejected(`bib ${values.bib} is on more than one entrant of this race`);
      }
      const entrant = byBib.get(values.bib);
      if (!entrant) throw new Rejected(`no entrant of this race has bib ${values.bib}`);

      // Two rows for one entrant is not an upsert, it is a mistake: the second
      // would overwrite the first and both would be reported as successes, so
      // the file looks imported and one row is simply gone.
      if (seen.has(entrant.id)) {
        throw new Rejected(`bib ${values.bib} already appears on line ${seen.get(entrant.id)}`);
      }
      seen.set(entrant.id, line);

      const existing = existingByRegistration.get(entrant.id);
      const payload = {
        ...values,
        registration_id: entrant.id,
        event_activity_id: Number(eventActivityId),
      };

      if (existing) {
        payload.id = existing.id;
        // WHICH SOURCE depends on whether the athlete recorded this run
        // themselves, NOT on whether a row happened to exist. A row with a
        // recording attached is now genuinely both — the file's time is
        // official and the recording is the detail underneath — so it becomes
        // app + bib timed. A row with no recording is a previous upload of
        // this same file, and re-uploading a corrected file must leave it the
        // organizer import it has always been.
        //
        // activity_id is never sent either way, so the recording survives the
        // import that made the time official.
        const recorded = existing.activity_id !== null && existing.activity_id !== undefined;
        const become = recorded
          ? timingSourceIds.applicationBibTimed
          : timingSourceIds.organizerImport;
        if (become) payload.timing_source_id = become;
      } else if (timingSourceIds.organizerImport) {
        payload.timing_source_id = timingSourceIds.organizerImport;
      }

      rowsOut.push(payload);
    } catch (e) {
      if (e instanceof Rejected) rejections.push({ line, reason: e.message });
      else rejections.push({ line, reason: e.message || 'could not be read' });
    }
  });

  return { rows: rowsOut, rejections };
}

/**
 * One chunk's rows as batch operations.
 *
 * Inserts and updates travel as SEPARATE operations: the platform refuses an
 * operation whose rows mix the two, because TableManager decides insert from
 * update for the payload as a whole and a mixed list would take the insert path
 * carrying caller-supplied primary keys.
 */
function operationsFor(rows) {
  const updates = rows.filter((r) => r.id !== undefined);
  const inserts = rows.filter((r) => r.id === undefined);
  const operations = [];
  if (updates.length) operations.push({ table: RESULT_TABLE, data: updates });
  if (inserts.length) operations.push({ table: RESULT_TABLE, data: inserts });
  return operations;
}

/** Write the rows in chunks, reporting progress after each one. */
export async function saveInChunks(api, rows, { onProgress, chunk = CHUNK } = {}) {
  const chunks = Math.ceil(rows.length / chunk) || 0;
  let written = 0;
  for (let i = 0; i < rows.length; i += chunk) {
    const slice = rows.slice(i, i + chunk);
    // eslint-disable-next-line no-await-in-loop
    const resp = await api.apiCall('/arcee/application/table/save/records/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...scope(), operations: operationsFor(slice) }),
    });
    if (!resp.ok) {
      // eslint-disable-next-line no-await-in-loop
      const why = await readError(resp, 'A batch of rows was refused.');
      // Each batch is one transaction, so the batch that failed wrote nothing.
      // Saying how far it got is what makes the re-run obviously safe.
      throw new Error(
        `${written} of ${rows.length} rows were saved before this stopped. ${why} `
        + 'Uploading the same file again will finish the job without duplicating anything.',
      );
    }
    written += slice.length;
    if (onProgress) {
      onProgress({ written, total: rows.length, chunk: Math.floor(i / chunk) + 1, chunks });
    }
  }
  return written;
}
